import {
  ColumnDef,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import {keyBy} from 'lodash';
import React, {FC, ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import {useVirtual} from 'react-virtual';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {RendererOf} from '../../../libs/shared/core/src/helpers/react/reactHelpers';
import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {PopoverCoordinator} from '../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {flexCenter} from '../../../libs/shared/ui/src/styles/mixins';
import {getReactTableMeta, makeReactTableMeta} from '../../helpers/reactTableMeta';
import {
  brandDataFieldsTableColumnId,
  DataFieldsTableColumnId,
  DataFieldsTableColumnModel,
  DataFieldType,
  isDataFieldsTableColumnListType,
} from '../../models/api/dataFieldsTableColumnModel';
import {
  DataFieldsTableData,
  DataFieldsTableDryRunResultModel,
  dataFieldsTableListValueSeparator,
  DataFieldsTableRowDryRunResultModel,
  RDataFieldsTableValue,
} from '../../models/api/dataFieldsTableModel';
import {tryParsePublicId} from '../../models/applications/publicIdTypes';
import {DataFieldsTableCellRenderer} from './dataFieldsTableCellRenderer';
import {DataFieldsTableColumnDialog} from './dataFieldsTableColumnDialog';
import {DataFieldsTableHeader} from './dataFieldsTableHeader';
import {DataFieldsTableOrderCell} from './dataFieldsTableOrderCell';
import {DataFieldsTableSearchContext, DataFieldsTableSearchContextData} from './dataFieldsTableSearchContext';
import {StyledDataFieldsTableCell, StyledDataFieldsTableHeader, StyledRemoveRowButton} from './styles';

/*
 * Constants.
 */

const DATA_TABLE_CELL_MIN_WIDTH = 250;
const DATA_TABLE_CELL_HEIGHT = 40;
const ORDER_CELL_WIDTH = 88;
const ORDER_COLUMN_ID = brandDataFieldsTableColumnId('__order');
const ADD_COLUMN_ID = brandDataFieldsTableColumnId('__add');

/*
 * Styles.
 */

const StyledTable = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  width: 100%;

  th {
    text-align: left;
  }

  > thead {
    position: sticky;
    top: 0;

    z-index: 1;
  }

  > tbody > tr {
    & ${StyledRemoveRowButton} {
      visibility: hidden;
    }

    &:hover ${StyledRemoveRowButton} {
      visibility: visible;
    }
  }

  > tfoot {
    position: sticky;
    bottom: 0;
  }
`;

const StyledWrapper = styled.div`
  max-height: 100%;
  width: min-content;
  min-width: 100%;
  overflow: auto;
`;

export const StyledTableFooterCell = styled.td`
  background-color: ${(p) => p.theme.greys.white};
`;

const StyledFooterButtonWrapper = styled.div`
  ${flexCenter};
  height: ${DATA_TABLE_CELL_HEIGHT}px;
  padding: 5px 0 4px 7px;
  width: 100%;
`;

const StyledCellWrapper = styled.div`
  ${flexCenter};
  justify-content: center;
`;

/*
 * Props.
 */

export interface DataFieldsTableProps {
  columns: Array<DataFieldsTableColumnModel>;
  rows: Array<DataFieldsTableData>;

  onUpdateCell: (row: number, column: DataFieldsTableColumnModel, value: string | undefined) => void;
  onAddColumn: (data: Omit<DataFieldsTableColumnModel, 'id'>) => void;
  onEditColumn: (
    id: DataFieldsTableColumnId,
    columnData: Partial<DataFieldsTableColumnModel>,
    options?: {
      shouldClearData?: boolean;
    },
  ) => void;
  onDeleteColumn: (id: DataFieldsTableColumnId) => void;
  columnValidator: RendererOf<Partial<DataFieldsTableColumnModel>>;

  onAddRow: () => void;
  onDeleteRow: (index: number) => void;
  onReorderRow?: (currentIndex: number, newIndex: number) => void;

  dryRunTable?: DataFieldsTableDryRunResultModel;
  onSelectRowDryRun: (row: DataFieldsTableRowDryRunResultModel) => void;
  shouldDisplayDryRunColumn: boolean;

  emptyState?: ReactNode;

  searchText?: string;
}

declare module '../../helpers/reactTableMeta' {
  interface ReactTableMetaValueMap {
    'data-fields-table': {
      columns: Record<string, DataFieldsTableColumnModel>;
      updateData: (rowIndex: number, columnId: string, value: unknown) => void;
      onAddColumn: (data: Omit<DataFieldsTableColumnModel, 'id'>) => void;
      onEditColumn: (
        id: DataFieldsTableColumnId,
        name: Partial<DataFieldsTableColumnModel>,
        options?: {
          shouldClearData?: boolean;
        },
      ) => void;
      onDeleteColumn: (id: DataFieldsTableColumnId) => void;
      onDeleteRow: (index: number) => void;
      onReorderRow?: (currentIndex: number, newIndex: number) => void;
      columnValidator: RendererOf<Partial<DataFieldsTableColumnModel>>;
    };
  }
}

/*
 * Constants.
 */

const defaultColumn: Partial<ColumnDef<DataFieldsTableData>> = {
  header: ({table, column: tableColumn}) => {
    const meta = getReactTableMeta(table, 'data-fields-table');
    const column = meta.columns[tableColumn.id];
    if (!column) {
      return null;
    }

    return (
      <DataFieldsTableHeader
        column={column}
        onEditColumn={(newColumn) => meta.onEditColumn(column.id, newColumn)}
        columnValidator={meta.columnValidator}
        onDeleteColumn={() => meta.onDeleteColumn(column.id)}
      />
    );
  },
  cell: ({getValue, row: {index}, column: tableColumn, table}) => {
    const value = getValue();
    const meta = getReactTableMeta(table, 'data-fields-table');

    if (value !== undefined && typeof value !== 'string') {
      return null;
    }

    const column = meta.columns[tableColumn.id];

    if (!column) {
      return null;
    }

    return (
      <DataFieldsTableCellRenderer
        value={value}
        column={column}
        listSeparator=", "
        onChange={(newValue) => meta.updateData(index, tableColumn.id, newValue)}
      />
    );
  },
};

/*
 * Component.
 */

export const DataFieldsTable: FC<DataFieldsTableProps> = ({
  columns: baseColumns,
  rows: data,
  onUpdateCell,
  onEditColumn,
  onAddColumn,
  onDeleteColumn,
  onReorderRow,
  onAddRow,
  onDeleteRow,
  columnValidator,
  emptyState,
  searchText,
}) => {
  const hasReorderRow = Boolean(onReorderRow);

  const tableColumns = useMemo<Array<ColumnDef<DataFieldsTableData>>>(() => {
    const orderColumn: ColumnDef<DataFieldsTableData> = {
      id: ORDER_COLUMN_ID,
      // eslint-disable-next-line react/no-unstable-nested-components
      header: () => <Trans id="data-fields-table-order-column-name">Order</Trans>,
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({row: {index}, table}) => (
        <DataFieldsTableOrderCell
          order={index + 1}
          onChange={(newOrder) =>
            getReactTableMeta(table, 'data-fields-table').onReorderRow?.(index, newOrder - 1)
          }
        />
      ),
      size: ORDER_CELL_WIDTH,
      maxSize: ORDER_CELL_WIDTH,
    };

    const dataColumns = baseColumns.map(
      (column): ColumnDef<DataFieldsTableData> => ({
        id: column.id,
        accessorKey: column.id,
        minSize: DATA_TABLE_CELL_MIN_WIDTH,
      }),
    );

    const addColumnColumn: ColumnDef<DataFieldsTableData> = {
      id: ADD_COLUMN_ID,
      // eslint-disable-next-line react/no-unstable-nested-components
      header: ({table}) => (
        <PopoverCoordinator
          renderButton={(isOpen) => (
            <IconButton
              iconName="plusCircle"
              label={<Trans id="data-fields-table-add-column-icon-label">Add column</Trans>}
              isOpen={isOpen}
              hasHover={isOpen}
            />
          )}
          renderModal={(popoverProps) => (
            <DataFieldsTableColumnDialog
              {...popoverProps}
              mode="create"
              columnValidator={getReactTableMeta(table, 'data-fields-table').columnValidator}
              onSubmit={(newColumn) =>
                getReactTableMeta(table, 'data-fields-table').onAddColumn({
                  name: '',
                  type: DataFieldType.STRING,
                  ...newColumn,
                })
              }
            />
          )}
        />
      ),
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({row: {index}, table}) => (
        <StyledRemoveRowButton
          tabIndex={-1}
          onClick={() => getReactTableMeta(table, 'data-fields-table')?.onDeleteRow(index)}
          label={<Trans id="data-fields-table-remove-row-button-label">Remove row</Trans>}
          iconName="trashSmall"
        />
      ),
      maxSize: 45,
    };

    if (hasReorderRow) {
      return [orderColumn, ...dataColumns, addColumnColumn];
    }
    return [...dataColumns, addColumnColumn];
  }, [baseColumns, hasReorderRow]);

  const columnRecord = useMemo(() => keyBy(baseColumns, 'id'), [baseColumns]);

  const [searchValueStore, setSearchValueStore] = useState<Record<string, string | undefined>>({});
  const dataFieldsSearchContext = useMemo<DataFieldsTableSearchContextData>(
    () => ({
      addSearchData: (values) =>
        setSearchValueStore((current) => ({
          ...current,
          ...Object.fromEntries(values.map(({publicId, searchValue}) => [publicId, searchValue])),
        })),
    }),
    [],
  );

  const globalFilterFn = useDataFieldsTableGlobalFilter(columnRecord, searchValueStore);

  const table = useReactTable({
    data,
    columns: tableColumns,
    defaultColumn,
    globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),

    meta: makeReactTableMeta('data-fields-table', {
      columns: columnRecord,
      updateData: (index, columnId, value) => {
        const column = columnRecord[columnId];
        if (!column || (value !== undefined && typeof value !== 'string')) {
          return;
        }
        onUpdateCell(index, column, value);
      },
      onAddColumn,
      onEditColumn,
      onDeleteColumn,
      columnValidator,
      onDeleteRow,
      onReorderRow,
    }),
  });

  useEffect(() => table.setGlobalFilter(searchText), [data, table.setGlobalFilter, searchText]);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const {rows} = table.getRowModel();

  const rowVirtualizer = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    overscan: 5,
    estimateSize: estimateRowSize,
  });
  const {virtualItems: virtualRows, totalSize} = rowVirtualizer;

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

  return (
    <DataFieldsTableSearchContext.Provider value={dataFieldsSearchContext}>
      <StyledWrapper ref={tableContainerRef}>
        <StyledTable>
          <colgroup>
            {hasReorderRow && <col width={ORDER_CELL_WIDTH} />}
            {baseColumns.map((column) => (
              <col key={column.id} />
            ))}
            <col width={44} />
          </colgroup>

          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <StyledDataFieldsTableHeader key={header.id}>
                    <StyledCellWrapper>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </StyledCellWrapper>
                  </StyledDataFieldsTableHeader>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <td style={{height: `${paddingTop}px`}} />
              </tr>
            )}

            {data.length === 0 && (
              <tr>
                <td colSpan={tableColumns.length}>{emptyState}</td>
              </tr>
            )}

            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index] as Row<DataFieldsTableData>;
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <StyledDataFieldsTableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </StyledDataFieldsTableCell>
                  ))}
                </tr>
              );
            })}
            {paddingBottom > 0 && (
              <tr>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <td style={{height: `${paddingBottom}px`}} />
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <StyledTableFooterCell colSpan={tableColumns.length}>
                <StyledFooterButtonWrapper>
                  <IconButton
                    onClick={onAddRow}
                    iconName="plusCircle"
                    label={<Trans id="data-fields-table-add-row-button-label">Add row</Trans>}
                    placement="left"
                  />
                </StyledFooterButtonWrapper>
              </StyledTableFooterCell>
            </tr>
          </tfoot>
        </StyledTable>
      </StyledWrapper>
    </DataFieldsTableSearchContext.Provider>
  );
};

function useDataFieldsTableGlobalFilter(
  columnRecord: Record<string, DataFieldsTableColumnModel>,
  searchValueStore: Record<string, string | undefined>,
): FilterFn<DataFieldsTableData> {
  return (row, columnId, filterValue) => {
    const baseColumn = columnRecord[columnId];
    if (!baseColumn || filterValue === undefined || typeof filterValue !== 'string') {
      return true;
    }

    const value = row.getValue(columnId);

    const searchValue = serializeDataTableValueForSearch(
      value,
      baseColumn.type,
      (publicId) => searchValueStore[publicId],
    );

    if (!searchValue) {
      return false;
    }

    return searchValue.toLowerCase().includes(filterValue.toLowerCase());
  };
}

function serializeDataTableValueForSearch(
  value: unknown,
  type: DataFieldType,
  getResourceSearchValue: (publicId: string) => string | undefined,
): string | undefined {
  if (!RDataFieldsTableValue.is(value) || value === undefined) {
    return undefined;
  }

  if (type === DataFieldType.STRING || type === DataFieldType.NUMBER || type === DataFieldType.LANGUAGE) {
    return value;
  }

  if (type === DataFieldType.BOOLEAN) {
    return value === 'true' ? 'yes' : 'no';
  }

  const privateId = tryParsePublicId(value);
  if (!privateId && !isDataFieldsTableColumnListType(type)) {
    return undefined;
  }

  switch (type) {
    case DataFieldType.CANNED_ANSWER:
    case DataFieldType.INBOX:
    case DataFieldType.TAG:
    case DataFieldType.TEAMMATE:
      return getResourceSearchValue(value);
    case DataFieldType.TAG_LIST:
      return serializeListValueForSearch(value, DataFieldType.TAG, getResourceSearchValue);
    case DataFieldType.INBOX_LIST:
      return serializeListValueForSearch(value, DataFieldType.INBOX, getResourceSearchValue);
    case DataFieldType.TEAMMATE_LIST:
      return serializeListValueForSearch(value, DataFieldType.TEAMMATE, getResourceSearchValue);
    default:
      return undefined;
  }
}

function serializeListValueForSearch(
  value: string,
  type: DataFieldType,
  getResourceSearchValue: (publicId: string) => string | undefined,
) {
  return value
    .split(dataFieldsTableListValueSeparator)
    .map((resource) => serializeDataTableValueForSearch(resource, type, getResourceSearchValue))
    .join(dataFieldsTableListValueSeparator);
}

function estimateRowSize() {
  return 41;
}
